import dayjs from "dayjs";

import { Controller, UseFormReturn } from "react-hook-form";
import { X, AlertCircle } from "lucide-react";
import { Button } from "../../ui/button";
import { Card } from "../../ui/card";
import { Form } from "../../ui/form";
import { TIME_SLOTS } from "../../../shared/schema";
import { cn } from "../../../lib/utils";
import { getWeekDates, formatDate } from "../../../lib/utils";
import { CreateScheduleSlotCommand, CreateScheduleSlotDoctorItem, DoctorDto } from "../../../web-api-client";
import FormSelect from "../../booking/information/FormSelect";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { timeStringToDate } from "../../../utils/dateOptions";
import Can from "../../../auth/Can";
import { can, Perm } from "../../../auth/permissions";
import Alert from "../../ui/Alert";
import { useState } from "react";
import { DoctorSearchAddTable } from "./DoctorSearchAddTable";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  format,
} from "date-fns";
import { vi } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";

interface CreateSlotSidebarProps {
  onClose: () => void;
  currentDate: Date;
  form: UseFormReturn<CreateScheduleSlotCommand>;
  selectedTimeSlot: string | null;
  onTimeSlotClick: (slot: (typeof TIME_SLOTS)[number]) => void;
  onDateToggle: (dateStr: string, checked: boolean) => void;
  onSubmit: (payload: CreateScheduleSlotCommand) => void;
  conflicts: string[];
  hasConflicts: boolean;
  isCheckingConflicts: boolean;
  doctorSuggestions: DoctorDto[];
  isDoctorLoading: boolean;
  onDoctorSearchChange: (query: string) => void;
  clinicOptions: any;
}
export function CreateSlotSidebar({
  currentDate,
  form,
  selectedTimeSlot,
  onTimeSlotClick,
  onDateToggle,
  onSubmit,
  conflicts,
  hasConflicts,
  isCheckingConflicts,
  onClose,
  doctorSuggestions,
  isDoctorLoading,
  onDoctorSearchChange,
  clinicOptions,
}: CreateSlotSidebarProps) {
  const [viewMonth, setViewMonth] = useState<Date>(currentDate);
  const [selectedDoctors, setSelectedDoctors] = useState<DoctorDto[]>([]);
  const [quotaByDoctorId, setQuotaByDoctorId] = useState<
    Record<string, number>
  >({});
  const [defaultQuota, setDefaultQuota] = useState(10);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDates = getWeekDates(weekStart);

  const enableCreate =
    form.getValues("clinicExtId") &&
    form.getValues("dates") &&
    form.getValues("dates")!.length > 0 &&
    selectedDoctors.length > 0 &&
    form.getValues("endTime") &&
    form.getValues("startTime");

  const canManage = can(Perm.Schedule_Manage);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const clinicExtId = Number(form.getValues("clinicExtId"));
    const dates = form.getValues("dates") ?? [];
    const startTime = form.getValues("startTime") ?? "";
    const endTime = form.getValues("endTime") ?? "";

    const doctorsPayload = selectedDoctors.map((d) => ((new CreateScheduleSlotDoctorItem({
      doctorExtId: d.doctorId!, 
      quota: quotaByDoctorId[String(d.doctorId)] ?? defaultQuota,
    }))));

    onSubmit(new CreateScheduleSlotCommand({
      clinicExtId,
      dates,
      startTime,
      endTime,
      doctors: doctorsPayload,
    }));
  };

  return (
    <div className="mt-[60px] fixed right-0 top-0 h-full w-screen max-w-[500px] bg-background border-l border-border shadow-xl flex flex-col animate-in slide-in-from-right duration-300 z-50">
      <div className="border-b border-border px-6 py-2 flex items-center justify-between bg-card">
        <h2 className="text-lg font-semibold text-foreground">
          Thêm lịch khám
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid="button-close-form"
          className="hover:bg-primary rounded-full hover:text-white"
        >
          <X className="h-4 w-4 " />
        </Button>
      </div>

      <Can perm={Perm.Schedule_Manage}>
        <div className="flex items-center justify-between flex-col flex-1 overflow-y-auto px-6 py-6">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Phòng khám <span className="text-red-500 ml-1">*</span>
                </span>
                <FormSelect
                  name="clinicExtId"
                  control={form.control}
                  options={clinicOptions}
                  placeholder="Chọn phòng khám"
                />
              </div>

              <div className="space-y-4">
                <span className="text-sm font-medium text-gray-700 mb-2">
                  Khung giờ <span className="text-red-500 ml-1">*</span>
                </span>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Controller
                      control={form.control}
                      name="startTime"
                      render={({ field }) => (
                        <TimePicker
                          ampm={false}
                          label="Từ"
                          value={timeStringToDate(field.value)}
                          onChange={(value) =>
                            field.onChange(
                              value ? dayjs(value).format("HH:mm") : "",
                            )
                          }
                          slotProps={{
                            textField: { size: "small" },
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="space-y-2">
                    <Controller
                      control={form.control}
                      name="endTime"
                      render={({ field }) => (
                        <TimePicker
                          ampm={false}
                          label="Đến"
                          value={timeStringToDate(field.value)}
                          onChange={(value) =>
                            field.onChange(
                              value ? dayjs(value).format("HH:mm") : "",
                            )
                          }
                          slotProps={{
                            textField: { size: "small" },
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.label}
                      type="button"
                      onClick={() => onTimeSlotClick?.(slot)}
                      className={cn(
                        "px-3 py-1.5 text-sm rounded-md border transition-colors",
                        selectedTimeSlot === slot.label
                          ? "bg-[#Edf5F0] border-[#C3DCCB] text-[#366A4D]"
                          : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50",
                      )}
                    >
                      <span>{slot.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span>Bác sĩ</span>
                  <span className="ml-2 text-red-500">*</span>
                </label>
                <DoctorSearchAddTable
                  suggestions={doctorSuggestions}
                  isLoading={isDoctorLoading}
                  onSearchChange={onDoctorSearchChange}
                  defaultQuota={defaultQuota}
                  selectedDoctors={selectedDoctors}
                  onChangeSelected={setSelectedDoctors}
                  quotaByDoctorId={quotaByDoctorId}
                  onChangeQuotaMap={setQuotaByDoctorId}
                />
                {/* <FormSelect
                  name="doctorExtId"
                  control={form.control}
                  options={doctorOptions}
                  placeholder="Chọn bác sĩ"
                /> */}
              </div>

              <div className="space-y-3">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Ngày khám <span className="text-red-500 ml-1">*</span>
                </span>

                {(() => {
                  const selected = new Set(form.getValues("dates") ?? []);

                  const monthStart = startOfMonth(viewMonth);
                  const monthEnd = endOfMonth(viewMonth);
                  const gridStart = startOfWeek(monthStart, {
                    weekStartsOn: 1,
                  });
                  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

                  const days = eachDayOfInterval({
                    start: gridStart,
                    end: gridEnd,
                  });

                  return (
                    <div className="rounded-xl border border-border bg-white overflow-hidden">
                      {/* Month header */}
                      <div className="flex items-center justify-between px-3 py-2 bg-muted/10">
                        <button
                          type="button"
                          className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-muted/30"
                          onClick={() => setViewMonth((d) => subMonths(d, 1))}
                          aria-label="Prev month"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>

                        <span className="text-sm font-semibold text-green-700">
                          {format(viewMonth, "MMMM yyyy", { locale: vi })}
                        </span>

                        <button
                          type="button"
                          className="h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-muted/30"
                          onClick={() => setViewMonth((d) => addMonths(d, 1))}
                          aria-label="Next month"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Weekdays */}
                      <div className="grid grid-cols-7 gap-px bg-border">
                        {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((w) => (
                          <div
                            key={w}
                            className="bg-white text-center text-xs font-semibold text-muted-foreground py-2"
                          >
                            {w}
                          </div>
                        ))}
                      </div>

                      {/* Days grid */}
                      <div className="grid grid-cols-7 gap-px bg-border">
                        {days.map((day) => {
                          const dateStr = formatDate(day);
                          const isSelected = selected.has(dateStr);
                          const isInMonth = isSameMonth(day, viewMonth);
                          const isToday = isSameDay(day, new Date());

                          return (
                            <button
                              key={dateStr}
                              type="button"
                              onClick={() => onDateToggle(dateStr, !isSelected)}
                              className={cn(
                                "relative bg-white h-10 text-sm font-medium flex items-center justify-center",
                                "hover:bg-muted/20 transition",
                                !isInMonth && "text-muted-foreground/40",
                                isToday &&
                                  "ring-1 ring-primary/40 z-[1] text-red-500",
                                isSelected && "bg-green-50",
                              )}
                            >
                              <span
                                className={cn(
                                  isSelected && "text-green-800 font-semibold",
                                )}
                              >
                                {format(day, "dd")}
                              </span>

                              {isSelected && (
                                <span className="absolute right-1 top-1 h-5 w-5 rounded-md bg-green-600 text-white inline-flex items-center justify-center">
                                  <Check className="h-3 w-3" />
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Selected summary */}
                      <div className="px-3 py-2 border-t border-border bg-muted/10 text-xs text-muted-foreground">
                        Đã chọn:{" "}
                        <span className="font-semibold text-foreground">
                          {selected.size}
                        </span>{" "}
                        ngày
                      </div>
                    </div>
                  );
                })()}
              </div>

              {hasConflicts && (
                <div className="w-[300px] bg-white fixed bottom-5 right-[410px] ">
                  <Card className="w-full border-destructive bg-destructive/10 p-4">
                    <div className="flex gap-3">
                      <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium text-destructive">
                          Phát hiện xung đột
                        </p>
                        <ul className="text-xs text-destructive/90 space-y-1">
                          {conflicts.map((conflict, idx) => (
                            <li key={idx}>• {conflict}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </form>
          </Form>
        </div>

        <div className="p-4 w-full mb-[60px] border-t border-primary">
          <Button
            onClick={handleSubmit}
            type="submit"
            className="w-full"
            disabled={
              hasConflicts || isCheckingConflicts || !enableCreate || !canManage
            }
            data-testid="button-create-appointment"
          >
            Tạo lịch
          </Button>
        </div>
      </Can>

      {!canManage && (
        <Alert
          variant="destructive"
          msg="Bạn không có quyền thêm/sửa lịch khám"
        />
      )}
    </div>
  );
}
